/* online-pet-profile.js — vanilla progressive enhancement for the Online Pet Profile page.
 * Replaces the framework runtime from the React prototype:
 *   1. the interactive ProfileBuilder island (#sn-online-pet-profile [data-sn-widget="ProfileBuilder"])
 *      — live-editable profile fields on the left, a finder's-view phone that updates instantly
 *        on the right, and a Privacy Mode toggle that swaps the phone between the full profile and
 *        the locked "contact owner" state.
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The page renders a faithful static snapshot server-side; this script enhances it.
 * Ported verbatim from ProfileBuilder.jsx + ProfileContent.jsx + illustrations.jsx.
 */
(function () {
  'use strict';

  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false;
  var T = function (en, de) { return SN_DE ? de : en; };

  // ---- SVG fragments (ported from illustrations.jsx) ------------------------
  // Stroke icons (lucide-style, 2px round stroke). Inner path markup matches ICON_PATHS.
  var ICON_PATHS = {
    arrow:  '<path d="M5 12h14M13 6l6 6-6 6"></path>',
    check:  '<path d="M20 6 9 17l-5-5"></path>',
    shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"></path><path d="m9 12 2 2 4-4"></path>',
    heart:  '<path d="M19 14c1.5-1.5 3-3.4 3-5.5A4.5 4.5 0 0 0 12 5 4.5 4.5 0 0 0 2 8.5c0 2.1 1.5 4 3 5.5l7 7Z"></path>',
    alert:  '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"></path><path d="M12 9v4M12 17h.01"></path>',
    phone:  '<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2Z"></path>',
  };
  function icon(name, size, color) {
    var c = color || 'currentColor';
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + c +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + ICON_PATHS[name] + '</svg>';
  }
  function pawMark(size, color) {
    var c = color || 'currentColor';
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="' + c + '" aria-hidden="true">' +
      '<ellipse cx="7" cy="9" rx="2" ry="2.6"></ellipse>' +
      '<ellipse cx="12" cy="7.4" rx="2" ry="2.7"></ellipse>' +
      '<ellipse cx="17" cy="9" rx="2" ry="2.6"></ellipse>' +
      '<path d="M12 11.2c2.7 0 4.8 2 4.8 4.2 0 1.8-1.6 2.8-3.3 2.5-.6-.1-1.2-.1-1.5-.1s-.9 0-1.5.1c-1.7.3-3.3-.7-3.3-2.5 0-2.2 2.1-4.2 4.8-4.2Z"></path></svg>';
  }

  // ---- Tiny DOM helper ------------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var a in opts.attrs) e.setAttribute(a, opts.attrs[a]);
    (kids || []).forEach(function (c) {
      if (c == null) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  // ---- ProfileBuilder widget ------------------------------------------------
  // Field style (PB_Field `common`): identical for input + textarea.
  var FIELD_CSS = 'width:100%;padding:11px 14px;border-radius:11px;border:1.5px solid var(--sn-line);' +
    'font-family:var(--sn-body);font-size:14.5px;color:var(--sn-ink);background:var(--sn-cream);' +
    'outline:none;box-sizing:border-box;resize:none';
  var LABEL_SPAN_CSS = 'display:block;font-size:11.5px;font-weight:600;color:var(--sn-ink-60);' +
    'text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px';

  function initBuilder(root) {
    // 2 useState: the profile object `p` and the `privacy` boolean.
    var p = {
      name: 'Bandit', breed: T('Border Collie · 3 yr', 'Border Collie · 3 J.'),
      phone: T('07700 900 461', '0170 9876543'), backup: T('Sam · 07700 900 821', 'Sam · 0170 1234567'),
      vet: T('Riverside Vets · 020 7946 0102', 'Tierarztpraxis Uferweg · 0221 1234567'), chip: T('956000010472283', '276098100123456'),
      medical: 'On daily joint supplement. Friendly but bolts at fireworks.',
    };
    var privacy = false;
    var dog = function () { return p.name || 'your pet'; };

    // PB_Field: <label><span/>{input|textarea}</label>
    function field(label, key, placeholder, textarea) {
      var control;
      if (textarea) {
        control = h('textarea', { css: FIELD_CSS, attrs: { placeholder: placeholder, rows: '2' } });
        control.value = p[key];
      } else {
        control = h('input', { css: FIELD_CSS, attrs: { placeholder: placeholder } });
        control.value = p[key];
      }
      control.addEventListener('input', function () {
        p[key] = control.value;
        renderPhone();
      });
      return h('label', { css: 'display:block' }, [
        h('span', { css: LABEL_SPAN_CSS }, [label]),
        control,
      ]);
    }

    // PB_PhoneRow: returns null when value is empty.
    function phoneRow(iconName, label, value) {
      if (!value) return null;
      return h('div', { css: 'display:flex;align-items:flex-start;gap:11px;padding:11px 0;border-bottom:1px solid var(--sn-line)' }, [
        h('div', { css: 'flex-shrink:0;width:30px;height:30px;border-radius:9px;background:var(--sn-cream-2);display:flex;align-items:center;justify-content:center', html: icon(iconName, 16, 'var(--sn-forest)') }),
        h('div', { css: 'min-width:0' }, [
          h('div', { css: 'font-size:11px;color:var(--sn-ink-60);font-weight:600;text-transform:uppercase;letter-spacing:0.05em' }, [label]),
          h('div', { css: 'font-size:14.5px;font-weight:500;color:var(--sn-ink);word-break:break-word' }, [value]),
        ]),
      ]);
    }

    // ---- Right column: the finder phone screen (privacy ? locked : full) ----
    function phoneScreen() {
      if (privacy) {
        return h('div', { cls: 'sn-msg-in', css: 'flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:18px;padding:30px;text-align:center' }, [
          h('div', { css: 'width:70px;height:70px;border-radius:999px;background:var(--sn-forest);display:flex;align-items:center;justify-content:center', html: icon('shield', 34, 'var(--sn-lime)') }),
          h('div', {}, [
            h('h3', { css: 'font-size:20px;margin-bottom:8px' }, ['This pet has an owner']),
            h('p', { css: 'font-size:14px;color:var(--sn-ink-60);line-height:1.55' }, [T('The owner has chosen Privacy Mode. Notify them via Supernormal and they’ll be in touch.', 'Der Halter hat den Privatsphäre-Modus aktiviert. Benachrichtigen Sie ihn über Supernormal, er meldet sich bei Ihnen.')]),
          ]),
          h('a', { cls: 'sn-btn sn-btn--primary', css: 'width:100%;justify-content:center;font-size:14.5px', html: icon('phone', 16, 'currentColor') + ' <span>' + T('Notify the owner', 'Halter benachrichtigen') + '</span>' }),
          h('span', { css: 'font-size:11.5px;color:var(--sn-ink-60)' }, ['Your details stay hidden until you reveal them.']),
        ]);
      }
      var rows = h('div', { css: 'padding:14px 20px' }, [
        h('a', { cls: 'sn-btn sn-btn--lime', css: 'justify-content:center;width:100%;margin-bottom:6px', html: icon('phone', 17, 'currentColor') + (SN_DE ? ' <span>Halter von ' + esc(dog()) + ' anrufen</span>' : ' <span>Call ' + esc(dog()) + '’s owner</span>') }),
      ]);
      [
        phoneRow('phone', 'Owner', p.phone),
        phoneRow('phone', 'Backup contact', p.backup),
        phoneRow('heart', 'Vet', p.vet),
        phoneRow('shield', 'Microchip', p.chip ? 'Chipped · ' + p.chip : ''),
        phoneRow('alert', 'Medical notes', p.medical),
      ].forEach(function (r) { if (r) rows.appendChild(r); });

      return h('div', { cls: 'sn-msg-in', css: 'flex:1;display:flex;flex-direction:column;overflow-y:auto' }, [
        h('div', { css: 'background:var(--sn-forest);color:var(--sn-cream);padding:32px 20px 18px;text-align:center' }, [
          h('div', { css: 'width:60px;height:60px;border-radius:999px;background:var(--sn-peach);margin:0 auto 10px;display:flex;align-items:center;justify-content:center', html: pawMark(30, 'var(--sn-forest)') }),
          h('h3', { css: 'font-size:21px;color:var(--sn-cream)' }, [p.name || 'Found me?']),
          h('p', { css: 'font-size:13px;color:rgba(255,248,240,0.8);margin-top:2px' }, [p.breed]),
        ]),
        rows,
      ]);
    }

    // The "Call X’s owner" label is the one spot a field value lands inside innerHTML,
    // so escape it the way React's {dog()} would (auto-escaping) to keep the markup safe.
    function esc(s) {
      return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    var phoneInner; // the .sn-msg-in screen container (re-rendered on edit/toggle)
    function renderPhone() {
      var fresh = phoneScreen();
      if (phoneInner && phoneInner.parentNode) {
        phoneInner.parentNode.replaceChild(fresh, phoneInner);
      }
      phoneInner = fresh;
    }

    // ---- Editor (left column) -----------------------------------------------
    var dogLabel = h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:18px' });
    dogLabel.textContent = dog() + '’s profile';

    var editorHeader = h('div', { css: 'display:flex;align-items:center;gap:12px;margin-bottom:22px' }, [
      h('span', { css: 'width:56px;height:56px;border-radius:16px;background:var(--sn-peach);display:flex;align-items:center;justify-content:center;flex-shrink:0', html: pawMark(28, 'var(--sn-forest)') }),
      h('div', {}, [
        dogLabel,
        h('button', { css: 'background:none;border:none;padding:0;cursor:pointer;font-size:13px;color:var(--sn-forest);font-weight:600;display:inline-flex;align-items:center;gap:5px', html: icon('check', 14, 'var(--sn-forest)') + ' ' + T('Add a photo', 'Foto hinzufügen') }),
      ]),
    ]);

    var nameField = field('Pet name', 'name', 'Name', false);
    // keep the editor header dog name in sync when the name changes
    nameField.querySelector('input').addEventListener('input', function (e) {
      dogLabel.textContent = (e.target.value || 'your pet') + '’s profile';
    });

    var gridFields = h('div', { css: 'display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px' }, [
      nameField,
      field('Breed & age', 'breed', 'Breed · age', false),
      field('Your number', 'phone', 'Phone', false),
      field('Backup contact', 'backup', 'Backup', false),
    ]);

    var stackFields = h('div', { css: 'display:flex;flex-direction:column;gap:14px;margin-bottom:18px' }, [
      field('Vet', 'vet', 'Vet practice & number', false),
      field('Microchip ID', 'chip', 'Chip number', false),
      field('Allergies, meds & medical notes', 'medical', 'Anything a finder or vet should know', true),
    ]);

    // Privacy toggle
    var knob = h('span', { css: 'display:block;width:24px;height:24px;border-radius:999px;background:#fff;transform:translateX(0);box-shadow:0 1px 3px rgba(0,0,0,0.25)' });
    var toggle = h('button', {
      attrs: { 'aria-pressed': 'false' },
      css: 'cursor:pointer;border:none;width:52px;height:30px;border-radius:999px;padding:3px;flex-shrink:0;background:var(--sn-line);position:relative',
      on: { click: function () {
        privacy = !privacy;
        toggle.setAttribute('aria-pressed', privacy ? 'true' : 'false');
        toggle.style.background = privacy ? 'var(--sn-forest)' : 'var(--sn-line)';
        knob.style.transform = privacy ? 'translateX(22px)' : 'translateX(0)';
        renderPhone();
      } },
    }, [knob]);

    var privacyRow = h('div', { css: 'display:flex;align-items:center;justify-content:space-between;gap:16px;background:var(--sn-cream-2);border-radius:14px;padding:14px 18px' }, [
      h('div', { css: 'display:flex;align-items:center;gap:11px' }, [
        h('span', { css: 'display:inline-flex', html: icon('shield', 20, 'var(--sn-forest)') }),
        h('div', {}, [
          h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:14.5px' }, [T('Privacy Mode', 'Privatsphäre-Modus')]),
          h('div', { css: 'font-size:12.5px;color:var(--sn-ink-60)' }, ['Hide details until you choose to reveal them']),
        ]),
      ]),
      toggle,
    ]);

    var editor = h('div', { css: 'padding:32px 38px' }, [editorHeader, gridFields, stackFields, privacyRow]);

    // ---- Right column wrapper ----------------------------------------------
    phoneInner = phoneScreen();
    var phoneScreenWrap = h('div', { css: 'background:var(--sn-cream);border-radius:30px;overflow:hidden;height:540px;position:relative;display:flex;flex-direction:column' }, [
      h('div', { css: 'position:absolute;top:10px;left:50%;transform:translateX(-50%);width:92px;height:22px;background:#1C1917;border-radius:999px;z-index:5' }),
      phoneInner,
    ]);

    var rightCol = h('div', { css: 'background:var(--sn-cream-2);padding:30px 32px;display:flex;flex-direction:column;align-items:center;gap:14px;border-left:1px solid var(--sn-line)' }, [
      h('div', { cls: 'sn-eyebrow', css: 'color:var(--sn-forest)' }, ['What a finder sees']),
      h('div', { css: 'width:290px;background:#1C1917;border-radius:40px;padding:11px;box-shadow:0 40px 80px -40px rgba(28,25,23,0.6)' }, [phoneScreenWrap]),
      h('p', { css: 'font-size:12px;color:var(--sn-ink-60);text-align:center;max-width:250px' }, ['Edit any field - the finder’s view updates instantly. No re-engraving, ever.']),
    ]);

    var grid = h('div', { css: 'display:grid;grid-template-columns:1.25fr 1fr;border-radius:28px;overflow:hidden;border:1px solid var(--sn-line);box-shadow:0 30px 70px -45px rgba(28,25,23,0.5);background:#fff' }, [editor, rightCol]);

    root.innerHTML = '';
    root.appendChild(grid);
  }

  // The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
  // Answers are rendered into the static body.html (for SEO/crawlability), so this
  // file only owns the ProfileBuilder widget.

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var page = document.getElementById('sn-online-pet-profile');
    if (!page) return;
    var builder = page.querySelector('[data-sn-widget="ProfileBuilder"]');
    if (builder) initBuilder(builder);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
